import { useEffect, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import type { z } from 'zod';

import { Button } from '@nesso/ui/primitives/button';
import { FormFields } from '@nesso/ui/primitives/form';

import {
    type CreateClientInput,
    createClientInputSchema,
    splitPrefillName,
    useCreateClientOptions,
} from '@/entities/client';
import { useConfirmDialogStore } from '@/shared/stores/confirm-dialog-store';
import { useSheetStore } from '@/shared/stores/sheet-store';
import {
    SheetBody,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetPanel,
    SheetTitle,
} from '@/shared/ui/panels/sheet-panel';

import { QuickAddClientNameFields } from './quick-add-client/quick-add-client-name-fields';

// Mounted once per page that carries search (research.md R10), so it never remounts between
// opens — the opener's intent travels through the sheet payload instead of props.
export function QuickAddClient() {
    const currentSheet = useSheetStore((state) => state.currentSheet);
    const closeCurrentSheet = useSheetStore((state) => state.closeCurrentSheet);
    const openConfirmDialog = useConfirmDialogStore((state) => state.open);
    const navigate = useNavigate();

    const isOpen = currentSheet?.type === 'clients/quick-add';
    const sheetData = isOpen ? currentSheet.data : undefined;
    const startSessionOnCreate = sheetData?.startSessionOnCreate ?? false;

    const createClientOptions = useCreateClientOptions();
    const createClientMutation = useMutation(createClientOptions);
    const isDirtyRef = useRef(false);

    const form = useForm<CreateClientInput>({
        mode: 'onChange',
        resolver: zodResolver(createClientInputSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            preferredFirstName: '',
        },
    });

    useEffect(() => {
        isDirtyRef.current = form.formState.isDirty;
    }, [form.formState.isDirty]);

    // The sheet stays mounted across opens, so `defaultValues` only ever apply once — the form is
    // reset explicitly every time the sheet opens, carrying in whatever the opener prefilled
    // (FR-020), or nothing at all when it opened with no data.
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-seed only on the transition to open — including sheetData would re-run on every keystroke that produced it and fight the therapist's own edits while the sheet stays open
    useEffect(() => {
        if (!isOpen) return;
        const { firstName, lastName } = splitPrefillName(sheetData?.prefillName ?? '');
        form.reset({ firstName, lastName, preferredFirstName: '' });
    }, [isOpen, form]);

    const onSubmit = async (data: z.input<typeof createClientInputSchema>) => {
        try {
            const result = await createClientMutation.mutateAsync(data);
            form.reset();
            closeCurrentSheet();
            if (startSessionOnCreate) {
                navigate({ to: '/recording', search: { clientId: result.id } });
            }
        } catch {
            // The error is handled by TanStack mutation
        }
    };

    const discardChanges = () => {
        form.reset();
        closeCurrentSheet();
    };

    const confirmDiscardChanges = () => {
        if (isDirtyRef.current) {
            openConfirmDialog({
                title: 'Discard unsaved changes?',
                description: 'You have unsaved changes. Are you sure you want to discard them?',
                confirmText: 'Discard',
                onConfirm: discardChanges,
            });
            return;
        }

        closeCurrentSheet();
    };

    return (
        <SheetPanel type="clients/quick-add" size="small" onRequestClose={confirmDiscardChanges}>
            <SheetHeader>
                <SheetTitle>New Client</SheetTitle>
                <SheetDescription className="sr-only">
                    {startSessionOnCreate
                        ? 'Create a new client to start a new session.'
                        : 'Create a new client.'}
                </SheetDescription>
            </SheetHeader>
            <FormProvider {...form}>
                <SheetBody className="pt-4">
                    <FormFields>
                        <QuickAddClientNameFields />
                    </FormFields>
                </SheetBody>
                <SheetFooter>
                    <Button type="button" variant="ghost" onClick={confirmDiscardChanges}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={createClientMutation.isPending}
                        onClick={form.handleSubmit(onSubmit)}
                    >
                        {startSessionOnCreate ? 'Start Dictation' : 'Add Client'}
                    </Button>
                </SheetFooter>
            </FormProvider>
        </SheetPanel>
    );
}
